import { useState, useEffect } from 'react'
import { useAdmin } from '@/contexts/AdminContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Shield, Save, Code, Tag, Loader2 } from 'lucide-react'

const Admin = () => {
  const {
    gtmId,
    headerScripts,
    bodyScripts,
    updateSettings,
    loading: contextLoading,
  } = useAdmin()

  const [formData, setFormData] = useState({
    gtmId: '',
    headerScripts: '',
    bodyScripts: '',
  })
  const [isSaving, setIsSaving] = useState(false)

  // Load initial data from context
  useEffect(() => {
    setFormData({
      gtmId,
      headerScripts,
      bodyScripts,
    })
  }, [gtmId, headerScripts, bodyScripts])

  useEffect(() => {
    document.title = 'Admin Panel | DP6.shop'
  }, [])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await updateSettings(formData)
    } catch (error) {
      // Error is handled in context
    } finally {
      setIsSaving(false)
    }
  }

  if (contextLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex items-center gap-3 mb-8">
        <div className="bg-primary/10 p-3 rounded-xl">
          <Shield className="h-8 w-8 text-primary" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <p className="text-muted-foreground">
            Gerenciamento de scripts e pixels de rastreamento
          </p>
        </div>
      </div>

      <div className="grid gap-8">
        {/* GTM Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5" /> Google Tag Manager
            </CardTitle>
            <CardDescription>
              Configure o ID do container GTM (ex: GTM-PJ6RTVXB). O script será
              injetado automaticamente no head e body.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="gtmId">GTM ID</Label>
              <div className="flex gap-4">
                <Input
                  id="gtmId"
                  name="gtmId"
                  placeholder="GTM-XXXXXX"
                  value={formData.gtmId}
                  onChange={handleChange}
                  className="font-mono"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Custom Scripts Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="h-5 w-5" /> Scripts Personalizados
            </CardTitle>
            <CardDescription>
              Adicione scripts personalizados como Facebook Pixel, Pinterest
              Tags, etc.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="headerScripts">Header Scripts</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Injetado no final do elemento <code>&lt;head&gt;</code>. Ideal
                para meta tags e pixels de prioridade alta.
              </p>
              <Textarea
                id="headerScripts"
                name="headerScripts"
                placeholder="<!-- Coloque seus scripts do head aqui -->"
                value={formData.headerScripts}
                onChange={handleChange}
                className="font-mono min-h-[150px] text-xs"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bodyScripts">Body Scripts</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Injetado logo após a tag <code>&lt;body&gt;</code>. Ideal para
                tags noscript ou scripts não bloqueantes.
              </p>
              <Textarea
                id="bodyScripts"
                name="bodyScripts"
                placeholder="<!-- Coloque seus scripts do body aqui -->"
                value={formData.bodyScripts}
                onChange={handleChange}
                className="font-mono min-h-[150px] text-xs"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button
            size="lg"
            onClick={handleSave}
            className="w-full md:w-auto"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Salvando...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" /> Salvar Configurações
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default Admin
